/**
 * ============================================================================
 * ESSENTIALS FLAVOR QUICK ADD - Interactive Card Functionality
 * Premium add-to-cart with cart drawer integration
 * ============================================================================
 */

class FlavorQuickAdd {
  constructor() {
    this.section = document.querySelector('.flavor-quick-add');
    if (!this.section) return;
    
    this.cards = this.section.querySelectorAll('.flavor-card');
    this.varietyCard = this.section.querySelector('.variety-pack-card');
    this.cart = document.querySelector('cart-drawer');
    
    this.init();
  }
  
  init() {
    this.injectPerformanceLayers();
    this.setupHoverSync();
    this.setupAddToCart();
    this.setupVarietyPack();
    this.setupIntersectionObserver();
  }

  // Inject blur-layer + hover-tint as separate compositor layers
  injectPerformanceLayers() {
    this.cards.forEach(card => {
      // Blur layer (keeps backdrop-filter off the main card element)
      if (!card.querySelector('.flavor-card__blur-layer')) {
        const blur = document.createElement('div');
        blur.className = 'flavor-card__blur-layer';
        card.insertBefore(blur, card.firstChild);
      }
      // Hover tint (opacity-only crossfade, no background repaint on card)
      if (!card.querySelector('.flavor-card__hover-tint')) {
        const tint = document.createElement('div');
        tint.className = 'flavor-card__hover-tint';
        card.insertBefore(tint, card.firstChild);
      }
    });
  }
  
  // ═══════════════════════════════════════════════════════════════════════
  // HOVER SYNC — JS-driven class ensures card + ATC button animate together
  // regardless of stacking context or backdrop-filter interference
  // ═══════════════════════════════════════════════════════════════════════

  setupHoverSync() {
    this.cards.forEach(card => {
      card.addEventListener('mouseenter', () => card.classList.add('is-hovered'));
      card.addEventListener('mouseleave', () => card.classList.remove('is-hovered'));
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ADD TO CART FUNCTIONALITY
  // ═══════════════════════════════════════════════════════════════════════
  
  setupAddToCart() {
    this.cards.forEach(card => {
      const popupBtn = card.querySelector('.flavor-card__atc--popup');
      const linkCta = card.querySelector('.flavor-card__atc:not(.flavor-card__atc--popup)');

      if (popupBtn) {
        popupBtn.addEventListener('click', (e) => this.handlePopupOpen(e, popupBtn));
        card.addEventListener('click', (e) => {
          if (e.target.closest('.flavor-card__atc')) return;
          popupBtn.click();
        });
        card.style.cursor = 'pointer';
      }

      if (linkCta && !popupBtn) {
        card.addEventListener('click', (e) => {
          if (e.target.closest('.flavor-card__atc')) return;
          linkCta.click();
        });
        card.style.cursor = 'pointer';
      }
    });
  }

  handlePopupOpen(e, button) {
    e.preventDefault();
    e.stopPropagation();

    // Resolve the real (untranslated) handle from the script-embedded map.
    // TLAB translates data attributes, turning "essentials-pro" into "essentiels-pro",
    // which causes the /products/{handle}.js fetch to 404.
    // Resolve untranslated handle. Priority:
    // 1. Handle map (product ID → canonical handle, embedded in <script> tag)
    // 2. URL path (always contains canonical handle on product pages)
    // 3. data-product-handle attribute (last resort, may be translated by TLAB)
    const productId = button.dataset.productId;
    const handleMap = window._ffpHandleMap || {};
    let realHandle = (productId && handleMap[productId]) || null;
    if (!realHandle) {
      const urlMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (urlMatch) realHandle = urlMatch[1];
    }
    if (!realHandle) realHandle = button.dataset.productHandle;

    const config = {
      handle: realHandle,
      flavor: button.dataset.flavorName,
      colors: {
        primary: button.dataset.colorPrimary,
        glow: button.dataset.colorGlow,
      },
      variantIds: {
        quarterly: button.dataset.variantQuarterly || null,
        monthly:   button.dataset.variantMonthly   || null,
        onetime:   button.dataset.variantOnetime    || null,
      },
    };

    if (window.FlavorFrequencyPopup) {
      window.FlavorFrequencyPopup.open(config);
    } else {
      // Script not yet loaded — wait for it
      const interval = setInterval(() => {
        if (window.FlavorFrequencyPopup) {
          clearInterval(interval);
          window.FlavorFrequencyPopup.open(config);
        }
      }, 50);
      setTimeout(() => clearInterval(interval), 5000);
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════
  // VARIETY PACK — hover + popup for the full-width card
  // ═══════════════════════════════════════════════════════════════════════

  setupVarietyPack() {
    if (!this.varietyCard) return;

    const popupBtn = this.varietyCard.querySelector('.flavor-card__atc--popup');
    if (popupBtn) {
      popupBtn.addEventListener('click', (e) => this.handlePopupOpen(e, popupBtn));
      this.varietyCard.addEventListener('click', (e) => {
        if (e.target.closest('.variety-pack-card__atc')) return;
        popupBtn.click();
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // INTERSECTION OBSERVER - Staggered entrance animations
  // ═══════════════════════════════════════════════════════════════════════
  
  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          window.requestAnimationFrame(() => {
            entry.target.classList.add('is-visible');
          });
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.01,
      rootMargin: '0px 0px 180px 0px'
    });
    
    this.cards.forEach((card, index) => {
      card.style.setProperty('--stagger-delay', `${index * 0.06}s`);
      card.classList.add('will-animate');
      observer.observe(card);
    });

    if (this.varietyCard) {
      this.varietyCard.style.setProperty('--stagger-delay', `${this.cards.length * 0.06}s`);
      this.varietyCard.classList.add('will-animate');
      observer.observe(this.varietyCard);
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FlavorQuickAdd());
} else {
  new FlavorQuickAdd();
}
